import { useState, useRef, useEffect, useCallback } from "react";

const API = "/api";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("dalfa_access_token");
  const headers = { ...(options.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
};

// Chave para localStorage
const STORAGE_KEY = "dalpha_prime_chat_history";
const SESSION_KEY = "dalpha_prime_session_id";

// Formatos suportados para upload
const SUPPORTED_FORMATS = {
  document: ['pdf', 'docx', 'doc', 'txt', 'md'],
  spreadsheet: ['xlsx', 'csv'],
  image: ['png', 'jpg', 'jpeg', 'webp', 'gif'],
  data: ['json']
};

const ALL_SUPPORTED = Object.values(SUPPORTED_FORMATS).flat();
const ACCEPT_STRING = ALL_SUPPORTED.map(ext => `.${ext}`).join(',');
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Determinar ícone baseado no tipo de ficheiro
const getFileIcon = (filename) => {
  const ext = filename.split('.').pop().toLowerCase();
  if (SUPPORTED_FORMATS.document.includes(ext)) return '📄';
  if (SUPPORTED_FORMATS.spreadsheet.includes(ext)) return '📊';
  if (SUPPORTED_FORMATS.image.includes(ext)) return '🖼️';
  if (SUPPORTED_FORMATS.data.includes(ext)) return '📋';
  return '📎';
};

// Formatar tamanho do ficheiro
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Gerar ID de sessão único
const generateSessionId = () => {
  return `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Obter ou criar ID de sessão
const getSessionId = () => {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

// Carregar mensagens do localStorage
const loadMessages = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validar estrutura
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Não carregar áudio do localStorage (muito grande)
        return parsed.map(msg => ({ ...msg, audio: null }));
      }
    }
  } catch (e) {
    console.error("Erro ao carregar histórico:", e);
  }
  return null;
};

// Salvar mensagens no localStorage
const saveMessages = (messages) => {
  try {
    // Não salvar áudio (muito grande)
    const toSave = messages.map(msg => ({ ...msg, audio: null }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.error("Erro ao salvar histórico:", e);
  }
};

/**
 * StreamingChat - Chat com Streaming Obrigatório (FÉNIX 2.0)
 * 
 * PROTOCOLO DE COMUNICAÇÃO:
 * - Streaming OBRIGATÓRIO: Todas as respostas são geradas como stream
 * - A primeira palavra aparece o mais rápido possível
 * - Usa Server-Sent Events (SSE) para comunicação em tempo real
 * - Suporte a áudio: reprodução automática ou sob demanda
 * - Persistência: Conversas são guardadas localmente e podem continuar
 */
const StreamingChat = () => {
  // Carregar mensagens salvas ou usar mensagem inicial
  const initialMessages = loadMessages() || [
    { 
      role: "assistant", 
      content: "Olá! Sou a Dalpha Prime, a tua assistente pessoal com memória persistente. As minhas respostas são transmitidas em tempo real. Como posso ajudar-te hoje?",
      streaming: false,
      audio: null
    }
  ];
  
  const [messages, setMessages] = useState(initialMessages);
  const [sessionId] = useState(getSessionId);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingMessageIdx, setPlayingMessageIdx] = useState(null);
  const [loadingAudioIdx, setLoadingAudioIdx] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("connected");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Salvar mensagens quando mudam
  useEffect(() => {
    if (messages.length > 1 || messages[0]?.role === "user") {
      saveMessages(messages);
    }
  }, [messages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Função para limpar conversa
  const clearConversation = useCallback(() => {
    const newMessages = [
      { 
        role: "assistant", 
        content: "Conversa limpa. Como posso ajudar-te?",
        streaming: false,
        audio: null
      }
    ];
    setMessages(newMessages);
    localStorage.removeItem(STORAGE_KEY);
    // Gerar nova sessão
    const newSessionId = generateSessionId();
    localStorage.setItem(SESSION_KEY, newSessionId);
  }, []);

  // Função para selecionar ficheiro
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validar extensão
    const ext = file.name.split('.').pop().toLowerCase();
    if (!ALL_SUPPORTED.includes(ext)) {
      alert(`Formato não suportado: .${ext}\n\nFormatos permitidos: ${ALL_SUPPORTED.join(', ')}`);
      event.target.value = '';
      return;
    }
    
    // Validar tamanho
    if (file.size > MAX_FILE_SIZE) {
      alert(`Ficheiro muito grande: ${formatFileSize(file.size)}\n\nTamanho máximo: ${formatFileSize(MAX_FILE_SIZE)}`);
      event.target.value = '';
      return;
    }
    
    setSelectedFile(file);
    
    // Criar preview para imagens
    if (SUPPORTED_FORMATS.image.includes(ext)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  }, []);

  // Função para remover ficheiro selecionado
  const clearSelectedFile = useCallback(() => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Função para fazer upload do ficheiro
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('chat_id', sessionId);
    formData.append('context', input);
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const response = await authFetch(`${API}/upload/file`, {
        method: 'POST',
        body: formData
      });
      
      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || 'Erro no upload');
      }
      
      setUploadProgress(100);
      return result;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const playAudio = (base64Audio, messageIdx = null) => {
    try {
      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      
      const audioBlob = new Blob(
        [Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
        setPlayingMessageIdx(null);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        setIsPlaying(false);
        setPlayingMessageIdx(null);
        audioRef.current = null;
      };
      
      setIsPlaying(true);
      setPlayingMessageIdx(messageIdx);
      audio.play().catch(() => {
        setIsPlaying(false);
        setPlayingMessageIdx(null);
      });
    } catch (e) {
      console.error("Error playing audio:", e);
      setIsPlaying(false);
      setPlayingMessageIdx(null);
    }
  };

  // Request audio for a specific message that doesn't have it
  const requestAudioForMessage = async (messageIdx) => {
    const message = messages[messageIdx];
    if (!message || message.role !== "assistant" || message.streaming) return;
    
    // If already has audio, just play it
    if (message.audio) {
      playAudio(message.audio, messageIdx);
      return;
    }
    
    setLoadingAudioIdx(messageIdx);
    
    try {
      const response = await authFetch(`${API}/chat/voice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message.content,
          chat_id: "web_user_1"
        })
      });
      
      const data = await response.json();
      
      if (data.audio) {
        // Store audio with the message
        setMessages(prev => {
          const updated = [...prev];
          updated[messageIdx] = { ...updated[messageIdx], audio: data.audio };
          return updated;
        });
        
        // Play the audio
        playAudio(data.audio, messageIdx);
      }
    } catch (error) {
      console.error("Error fetching audio:", error);
    } finally {
      setLoadingAudioIdx(null);
    }
  };

  // Stop audio playback
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setPlayingMessageIdx(null);
  };

  const sendMessageWithStreaming = async (e) => {
    e.preventDefault();
    if ((!input.trim() && !selectedFile) || isStreaming || isUploading) return;

    let userMessage = input.trim();
    let uploadedFile = null;
    
    // Se há ficheiro selecionado, fazer upload primeiro
    if (selectedFile) {
      try {
        uploadedFile = await uploadFile(selectedFile);
        
        // Adicionar menção ao ficheiro na mensagem
        const fileInfo = `📎 [Ficheiro: ${selectedFile.name}]`;
        if (userMessage) {
          userMessage = `${fileInfo}\n\n${userMessage}`;
        } else {
          userMessage = `${fileInfo}\n\nAnalisa este ficheiro por favor.`;
        }
      } catch (error) {
        // Mostrar erro mas continuar com a mensagem de texto se houver
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: `❌ Erro no upload: ${error.message}`,
          streaming: false,
          error: true
        }]);
        clearSelectedFile();
        return;
      }
    }
    
    setInput("");
    clearSelectedFile();
    
    // Add user message (with file attachment info if applicable)
    const userMsgObj = { 
      role: "user", 
      content: userMessage,
      attachment: uploadedFile ? {
        file_id: uploadedFile.file_id,
        filename: uploadedFile.filename,
        file_type: uploadedFile.file_type,
        preview: uploadedFile.preview
      } : null
    };
    setMessages(prev => [...prev, userMsgObj]);
    
    // Add placeholder for streaming response
    const placeholderIndex = messages.length + 1;
    setMessages(prev => [...prev, { 
      role: "assistant", 
      content: "", 
      streaming: true,
      audio: null
    }]);
    
    setIsStreaming(true);
    setConnectionStatus("streaming");
    
    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();
    
    try {
      // Preparar mensagem com contexto do ficheiro
      let messageToSend = userMessage;
      if (uploadedFile) {
        messageToSend = `[FICHEIRO ENVIADO: ${uploadedFile.filename} (ID: ${uploadedFile.file_id}, Tipo: ${uploadedFile.file_type})]
${uploadedFile.preview ? `\n[PREVIEW DO CONTEÚDO]:\n${uploadedFile.preview}\n` : ''}
${userMessage}`;
      }
      
      const response = await authFetch(`${API}/stream/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          message: messageToSend,
          chat_id: sessionId,
          include_audio: voiceEnabled,
          file_id: uploadedFile?.file_id || null,
          // Incluir histórico recente para contexto
          history: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content.substring(0, 500) // Limitar tamanho
          }))
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let audioData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'token') {
                fullContent += data.content;
                // Update message content in real-time
                setMessages(prev => {
                  const updated = [...prev];
                  const lastMsg = updated[updated.length - 1];
                  if (lastMsg && lastMsg.streaming) {
                    lastMsg.content = fullContent;
                  }
                  return updated;
                });
              } else if (data.type === 'status') {
                // Show status progress (URL extraction, etc.)
                setMessages(prev => {
                  const updated = [...prev];
                  const lastMsg = updated[updated.length - 1];
                  if (lastMsg && lastMsg.streaming && !fullContent) {
                    lastMsg.content = data.message || "⏳ A processar...";
                  }
                  return updated;
                });
              } else if (data.type === 'end') {
                // Final response received
                setMessages(prev => {
                  const updated = [...prev];
                  const lastMsg = updated[updated.length - 1];
                  if (lastMsg) {
                    lastMsg.content = data.full_response || fullContent;
                    lastMsg.streaming = false;
                    lastMsg.audio = audioData; // Store audio with message
                  }
                  return updated;
                });
              } else if (data.type === 'audio') {
                audioData = data.content;
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      // Play audio automatically if available and voice is enabled
      if (audioData && voiceEnabled) {
        const lastMsgIdx = messages.length; // The newly added message index
        playAudio(audioData, lastMsgIdx);
      }

      setConnectionStatus("connected");

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log("Stream cancelled");
      } else {
        console.error("Streaming error:", error);
        setMessages(prev => {
          const updated = [...prev];
          const lastMsg = updated[updated.length - 1];
          if (lastMsg && lastMsg.streaming) {
            lastMsg.content = "Erro na ligação. Tenta novamente.";
            lastMsg.streaming = false;
            lastMsg.error = true;
          }
          return updated;
        });
        setConnectionStatus("error");
      }
    } finally {
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  };

  const cancelStream = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const toggleVoice = () => {
    if (isPlaying) {
      stopAudio();
    }
    setVoiceEnabled(!voiceEnabled);
  };

  return (
    <div data-testid="streaming-chat-container" className="chat-container streaming-chat">
      <div className="chat-header">
        <div className="chat-header-icon">🦅</div>
        <div className="chat-header-info">
          <h3>Dalpha Prime</h3>
          <div className="chat-status-row">
            <span className={`chat-status ${connectionStatus}`}>
              {connectionStatus === "streaming" ? "A transmitir..." : 
               connectionStatus === "error" ? "Erro" : "Online"}
            </span>
            <span className="streaming-badge" title="Streaming activo">
              ⚡ Streaming
            </span>
          </div>
        </div>
        <button 
          onClick={toggleVoice}
          className={`voice-toggle-btn ${voiceEnabled ? 'active' : ''} ${isPlaying ? 'playing' : ''}`}
          title={voiceEnabled ? "Desligar voz" : "Ligar voz"}
          data-testid="voice-toggle-btn"
        >
          {isPlaying ? '🔊' : (voiceEnabled ? '🔈' : '🔇')}
        </button>
        <button 
          onClick={clearConversation}
          className="clear-chat-btn"
          title="Limpar conversa"
          data-testid="clear-chat-btn"
        >
          🗑️
        </button>
      </div>

      {/* Indicador de sessão */}
      {messages.length > 1 && (
        <div className="session-indicator" data-testid="session-indicator">
          💾 Conversa guardada automaticamente
        </div>
      )}

      <div className="chat-messages" data-testid="chat-messages">
        {messages.map((msg, idx) => (
          <div 
            key={idx} 
            className={`chat-message ${msg.role} ${msg.streaming ? 'streaming' : ''} ${msg.error ? 'error' : ''}`}
            data-testid={`chat-message-${idx}`}
          >
            {msg.role === "assistant" && (
              <span className="chat-avatar">{msg.error ? '❌' : '🦅'}</span>
            )}
            <div className="chat-bubble">
              {/* Show attachment if present */}
              {msg.attachment && (
                <div className="message-attachment" data-testid={`attachment-${idx}`}>
                  <span className="message-attachment-icon">{getFileIcon(msg.attachment.filename)}</span>
                  <span className="message-attachment-name">{msg.attachment.filename}</span>
                </div>
              )}
              {msg.content}
              {msg.streaming && <span className="streaming-cursor">▌</span>}
              {/* Audio button for assistant messages */}
              {msg.role === "assistant" && !msg.streaming && !msg.error && (
                <button
                  className={`message-audio-btn ${playingMessageIdx === idx ? 'playing' : ''} ${loadingAudioIdx === idx ? 'loading' : ''}`}
                  onClick={() => playingMessageIdx === idx ? stopAudio() : requestAudioForMessage(idx)}
                  title={playingMessageIdx === idx ? "Parar áudio" : "Ouvir mensagem"}
                  data-testid={`audio-btn-${idx}`}
                  disabled={loadingAudioIdx === idx}
                >
                  {loadingAudioIdx === idx ? '⏳' : (playingMessageIdx === idx ? '⏹️' : '🔊')}
                </button>
              )}
            </div>
            {msg.role === "user" && <span className="chat-avatar user">👤</span>}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessageWithStreaming} className="chat-input-form">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_STRING}
          onChange={handleFileSelect}
          className="file-upload-input"
          data-testid="file-input"
        />
        
        {/* File upload button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isStreaming || isUploading}
          className={`file-upload-btn ${selectedFile ? 'has-file' : ''}`}
          title="Anexar ficheiro"
          data-testid="file-upload-btn"
        >
          📎
        </button>
        
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={selectedFile ? `Mensagem sobre ${selectedFile.name}...` : "Escreve uma mensagem..."}
          disabled={isStreaming || isUploading}
          data-testid="chat-input"
          className="chat-input"
        />
        {isStreaming ? (
          <button 
            type="button"
            onClick={cancelStream}
            className="chat-cancel-btn"
            data-testid="chat-cancel-btn"
          >
            ⏹
          </button>
        ) : (
          <button 
            type="submit" 
            disabled={(!input.trim() && !selectedFile) || isUploading}
            data-testid="chat-send-btn"
            className="chat-send-btn"
          >
            {isUploading ? '⏳' : '➤'}
          </button>
        )}
      </form>
      
      {/* File preview */}
      {selectedFile && (
        <div className="file-preview" data-testid="file-preview">
          {filePreview ? (
            <img 
              src={filePreview} 
              alt="Preview" 
              className="message-image-preview"
              style={{ maxWidth: '60px', maxHeight: '60px' }}
            />
          ) : (
            <span className="file-preview-icon">{getFileIcon(selectedFile.name)}</span>
          )}
          <div className="file-preview-info">
            <div className="file-preview-name">{selectedFile.name}</div>
            <div className="file-preview-size">{formatFileSize(selectedFile.size)}</div>
          </div>
          <button 
            onClick={clearSelectedFile}
            className="file-preview-remove"
            title="Remover ficheiro"
            data-testid="file-remove-btn"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Upload progress */}
      {isUploading && (
        <div className="upload-progress" data-testid="upload-progress">
          <div className="upload-progress-bar">
            <div 
              className="upload-progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span className="upload-progress-text">{uploadProgress}%</span>
        </div>
      )}
    </div>
  );
};

export default StreamingChat;
